import React, { useState } from 'react';
import requiredIf from 'react-required-if';
import PropTypes from 'prop-types';
import VisibilityIcon from '@mui/icons-material/Visibility';
import _ from 'lodash';

import ButtonGroup from '~/components/core/Atomic/ButtonGroup';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { displayCommunicationCategories, stringCmp } from '~/Utils';

import { serverDateTimeToLocal } from '../../DateTimeUtils';
import { useClaim } from '../ClaimContainer';
import ClaimLink from '../ClaimLink';
import { CliveStoreStatusChip } from '../Clive/StatusChip/CliveStoreStatusChip';
import { SortableTable } from '../core';
import ExposureLabelChips from '../ExposureLabelChips';
import { useCms } from '../hooks/useCms';
import { AttachmentIcon, NotAnsweredIcon, VoicemailIcon } from '../icons';
import InlineIconButton from '../InlineIconButton';
import useOrganization from '../OrganizationContext';

import CommunicationCategoryChip from './CommunicationCategoryChip';
import CommunicationHandledStatusChip from './CommunicationHandledStatusChip';
import { getChannelDirectionColumns } from './CommunicationUtils';
import ViewCommunicationCardContainer from './ViewCommunicationCardContainer';

import { useStyles } from '../../assets/styles';

const getExtraIndicatorIcons = (comm) => {
  let iconsArr = [];

  if ((comm.channel === 'email' || comm.channel === 'sms') && comm.is_attachment_exist) {
    iconsArr.push({ name: 'Attachment', node: AttachmentIcon });
  }

  if (comm.channel === 'phone_call' && comm.is_no_answer) {
    if (comm.direction === 'Incoming' && comm.recording_stored_file_id) {
      iconsArr.push({ name: 'voicemail', node: VoicemailIcon });
    } else {
      iconsArr.push({ name: 'not-answered', node: NotAnsweredIcon });
    }
  }

  return (
    <>
      {iconsArr.map((icon) => {
        const Icon = icon.node;
        return <Icon size={20} key={icon.name} />;
      })}
    </>
  );
};

function CommunicationsTable({
  communications,
  selectedCommunicationId,
  onSelectCommunication,
  onRefreshCurrentPage,
  enableSelection,
  selectedList,
  onUpdatedSelected,
  maxHeight,
  autoPaginateRowsPerPage,
  addViewColumn,
  paginationProps,
  onSortByColumn,
  removeExposuresColumn,
  removeChannelColumn,
  removeDirectionColumn,
  removeAdjusterColumn,
  removeEmailDomainColumn,
  addClaimColumn,
  disableSortByUser,
  addSummaryColumn = true,
  showCLabel = true,
  addSubjectColumn = false,
  unreadRowsIds = [],
  counterViewText,
  showClive = false,
}) {
  const [communicationToShowInDialog, setCommunicationToShowInDialog] = useState(undefined);
  const [communicationIdsUpdating, setCommunicationIdsUpdating] = useState([]);
  const { userOrganization } = useCms();

  const { communicationCategories, communicationConfiguration } = useOrganization();
  const displayCategories = displayCommunicationCategories(communicationCategories) && Boolean(onRefreshCurrentPage);
  const showHandledStatus =
    communicationConfiguration?.is_handled_status_enabled === true && Boolean(onRefreshCurrentPage);

  const { isCliveFeatureEnabled } = useCliveClaimContext();

  const { classes } = useStyles();
  const { onClaimUpdate } = useClaim();

  let columnData = [
    // eslint-disable-next-line react/display-name
    {
      id: 'exposure_ids',
      numeric: false,
      leftPaddingOnly: false,
      label: 'Label',
      disableSort: true,
      specialCell: (comm) => <ExposureLabelChips claimObject={comm} disablePadding />,
    },
    ...getChannelDirectionColumns({ showCLabel }),
    { id: 'adjuster', numeric: false, disableSort: true, rightPaddingOnly: true, label: 'User' },
    {
      id: 'contact',
      numeric: false,
      rightPaddingOnly: true,
      label: 'Contact',
      maxWidthPercentage: 15,
      disableSort: true,
      specialCell: (comm) => {
        const toCount = comm.to_contacts_ids ? comm.to_contacts_ids.length : 0;
        const ccCount = comm.cc_contacts_ids ? comm.cc_contacts_ids.length : 0;
        const bccCount = comm.bcc_contacts_ids ? comm.bcc_contacts_ids.length : 0;
        const totalCount = toCount + ccCount + bccCount;

        return comm.contact
          ? comm.contact.full_name + (totalCount > 0 ? ` (+${totalCount})` : '')
          : comm.specific_identifier;
      },
      specialCmpFunc: (comm1, comm2) =>
        stringCmp(
          comm1.contact?.full_name || comm1.specific_identifier || '',
          comm2.contact?.full_name || comm2.specific_identifier || ''
        ),
    },

    {
      id: 'email_domain',
      label: 'Email Domain',
      numeric: false,
      disableSort: true,
    },

    {
      id: 'datetime',
      numeric: false,
      rightPaddingOnly: true,
      label: 'Time',
      nowrap: true,
      specialCell: (comm) => serverDateTimeToLocal(comm.datetime),
    },
    {
      id: 'category',
      label: 'Category',
      numeric: false,
      disableSort: true,
      rightPaddingOnly: true,
      isHidden: !displayCategories,
      specialCell: (comm) => (
        <CommunicationCategoryChip
          communication={comm}
          disabled={communicationIdsUpdating.includes(comm.id) || comm.id === selectedCommunicationId}
          setIsUpdating={(isUpdating) => {
            if (isUpdating) {
              setCommunicationIdsUpdating((prev) => [...prev, comm.id]);
            } else {
              setCommunicationIdsUpdating((prev) => prev.filter((id) => id !== comm.id));
            }
          }}
          onUpdate={async () => {
            await onRefreshCurrentPage();
          }}
        />
      ),
    },
    {
      id: 'extra_indicator',
      rightPaddingOnly: true,
      numeric: false,
      disableSort: true,
      label: '',
      specialCell: (comm) => getExtraIndicatorIcons(comm),
    },
  ];

  const shouldShowCliveStatusChip = React.useCallback(
    (communication) => {
      const { channel } = communication;

      if (channel === 'email') {
        return isCliveFeatureEnabled(CONFIGURATION_FEATURES_NAMES.CLIVE_EMAIL_SUMMARY);
      } else if (channel === 'phone_call') {
        return Boolean(communication.recording_stored_file_id);
      }
      return false;
    },
    [isCliveFeatureEnabled]
  );

  // Conditionally add Clive's Analysis column if feature flag is enabled
  if (showClive) {
    columnData.push({
      id: 'clive_status',
      label: `Clive's Analysis`,
      numeric: false,
      disableSort: true,
      specialCell: (comm) => {
        return shouldShowCliveStatusChip(comm) ? (
          <CliveStoreStatusChip status={comm.clive_store_information?.processing_status} />
        ) : null;
      },
    });
  }

  if (addSummaryColumn) {
    columnData.push({
      id: 'summary',
      label: 'Summary',
      numeric: false,
      disableSort: true,
      maxWidthPercentage: 25,
      overflow: true,
    });
  }

  columnData.push({
    id: 'mark_as_handled',
    label: 'Mark as handled',
    numeric: false,
    disableSort: true,
    width: 120,
    isHidden: !showHandledStatus,
    specialCell: (comm) => {
      if (comm.direction !== 'Incoming') {
        return null;
      }
      return (
        <CommunicationHandledStatusChip
          communicationId={comm.id}
          isHandled={comm.is_handled === true}
          displayText={false}
          disabled={
            communicationIdsUpdating.includes(comm.id) ||
            comm.id === selectedCommunicationId ||
            comm.id === communicationToShowInDialog?.id
          }
          setIsUpdating={(isUpdating) => {
            if (isUpdating) {
              setCommunicationIdsUpdating((prev) => [...prev, comm.id]);
            } else {
              setCommunicationIdsUpdating((prev) => prev.filter((id) => id !== comm.id));
            }
          }}
          onUpdate={onRefreshCurrentPage}
        />
      );
    },
  });

  if (addSubjectColumn) {
    columnData = columnData.concat([
      { id: 'subject', numeric: false, label: 'Subject', disableSort: true, maxWidthPercentage: 25, overflow: true },
    ]);
  }

  if (addViewColumn) {
    columnData = columnData.concat([
      {
        id: 'view_action',
        width: 25,
        specialCell: (communication) => (
          <InlineIconButton icon={VisibilityIcon} onClick={() => setCommunicationToShowInDialog(communication)} />
        ),
      },
    ]);
  }

  if (addClaimColumn) {
    columnData.unshift({
      id: 'claim',
      label: 'Claim',
      width: 25,
      // eslint-disable-next-line react/display-name
      specialCell: (communication) =>
        communication.claim_id ? (
          <ClaimLink claimId={communication.claim_id} linkText={communication.claim_id_display} />
        ) : null,
    });
  }

  if (removeExposuresColumn) {
    columnData = columnData.filter((column) => column.id !== 'exposure_ids');
  }

  if (removeChannelColumn) {
    columnData = columnData.filter((column) => column.id !== 'channel');
  }

  if (removeDirectionColumn) {
    columnData = columnData.filter((column) => column.id !== 'direction');
  }

  if (removeAdjusterColumn) {
    columnData = columnData.filter((column) => column.id !== 'adjuster');
  }

  if (!userOrganization?.is_suborgs_domains_enabled || removeEmailDomainColumn) {
    columnData = columnData.filter((column) => column.id !== 'email_domain');
  }

  const handleUpdateSelectedCommunicationsList = (comm_id, value) => {
    if (value) {
      if (!selectedList.includes(comm_id)) {
        onUpdatedSelected([...selectedList, comm_id]);
      }
    } else {
      onUpdatedSelected(_.filter(selectedList, (id) => id !== comm_id));
    }
  };

  let communicationColumns = columnData;
  if (enableSelection) {
    const communicationSelectionColumn = {
      id: 'select_comm',
      disableSort: true,
      width: 20,
      disablePadding: true,
      // eslint-disable-next-line react/display-name
      specialCell: (comm) => (
        <Checkbox
          // eslint-disable-next-line react/prop-types
          checked={selectedList.includes(comm.id)}
          onChange={(event) => handleUpdateSelectedCommunicationsList(comm.id, event.target.checked)}
        />
      ),
    };

    communicationColumns = [communicationSelectionColumn].concat(columnData);
  }

  const handleCommunicationClick = (communicationId) => {
    if (enableSelection) {
      onSelectCommunication(
        selectedList.includes(communicationId)
          ? _.without(selectedList, communicationId)
          : [...selectedList, communicationId]
      );
    } else {
      onSelectCommunication(communicationId);
    }
  };

  return (
    <>
      {enableSelection && (
        <div className={classes.cardDivRow}>
          <Grid container>
            <Grid item md={8}>
              <Typography variant="subtitle1">
                <strong>{selectedList.length}</strong>&nbsp; communications are selected
              </Typography>
            </Grid>
            <Grid item md={4}>
              <ButtonGroup variant="text" size="small" className={classes.inLineButtonsContainer}>
                <Button
                  onClick={() =>
                    onUpdatedSelected(
                      _.union(
                        selectedList,
                        communications.map((comm) => comm.id)
                      )
                    )
                  }
                >
                  All
                </Button>
                <Button
                  onClick={() =>
                    onUpdatedSelected(
                      selectedList.filter((comm_id) => !communications.find((comm) => comm.id === comm_id))
                    )
                  }
                >
                  None
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </div>
      )}
      <SortableTable
        rows={communications}
        columns={communicationColumns}
        onRowClick={handleCommunicationClick}
        selectedRowsIds={
          enableSelection ? [...(selectedList || []), ...unreadRowsIds] : [selectedCommunicationId, ...unreadRowsIds]
        }
        boldRowsIds={unreadRowsIds}
        defaultOrderColumn={communicationColumns.findIndex((column) => column.id === 'datetime')}
        maxHeight={maxHeight}
        paginationProps={paginationProps}
        onSortByColumn={onSortByColumn}
        autoPaginateRowsPerPage={autoPaginateRowsPerPage}
        order="desc"
        stickyHeader
        disableSortByUser={disableSortByUser}
        paginationLocation="both"
        counterViewText={counterViewText}
      />
      {communicationToShowInDialog && (
        <ViewCommunicationCardContainer
          claimId={communicationToShowInDialog.claim_id}
          communicationId={communicationToShowInDialog.id}
          onClose={() => setCommunicationToShowInDialog(undefined)}
          onUpdate={onClaimUpdate}
          isDialog
          displayAttachClaim={false}
          showClive={showClive}
        />
      )}
    </>
  );
}

CommunicationsTable.propTypes = {
  communications: PropTypes.array.isRequired,
  selectedCommunicationId: PropTypes.number,
  onSelectCommunication: PropTypes.func.isRequired,
  onRefreshCurrentPage: PropTypes.func,
  enableSelection: PropTypes.bool,
  selectedList: requiredIf(PropTypes.array, (props) => props.enableSelection),
  onUpdatedSelected: requiredIf(PropTypes.func, (props) => props.enableSelection),
  maxHeight: PropTypes.string,
  autoPaginateRowsPerPage: PropTypes.number,
  addViewColumn: PropTypes.bool,
  removeExposuresColumn: PropTypes.bool,
  removeChannelColumn: PropTypes.bool,
  removeDirectionColumn: PropTypes.bool,
  removeAdjusterColumn: PropTypes.bool,
  removeEmailDomainColumn: PropTypes.bool,
  addClaimColumn: PropTypes.bool,
  paginationProps: PropTypes.object,
  onSortByColumn: PropTypes.func,
  disableSortByUser: PropTypes.bool,
  showSummary: PropTypes.bool,
  showCLabel: PropTypes.bool,
  addSubjectColumn: PropTypes.bool,
  addSummaryColumn: PropTypes.bool,
  unreadRowsIds: PropTypes.array,
  counterViewText: PropTypes.node,
  showClive: PropTypes.bool,
};

export default CommunicationsTable;
